import { cloneElement, SyntheticEvent } from 'react';

import { usePickerDebounced } from '~/src/common/hooks/usePickerDebounced';
import I18n from '~/src/common/services/I18n';

import ConditionalTooltip from '../Tooltip';

import * as S from './layout';

interface PickerChildrenProps {
  onClick?: (e?: SyntheticEvent) => void;
}

interface PickerDebouncedProps {
  className?: string;
  cachedQuantity: number;
  availableQuantity: number;
  onQuantityChange: (quantity: number) => void;
  onDisabledClick: (quantity: number, increment: number) => void;
  size?: 'small' | 'large';
  emptyComponent: React.ReactElement<PickerChildrenProps>;
  children: React.ReactElement;
  enabled?: boolean;
  packSize?: number;
}

const PickerDebounced = ({
  className,
  onQuantityChange,
  onDisabledClick,
  cachedQuantity,
  availableQuantity,
  emptyComponent,
  children,
  size = 'small',
  enabled = true,
  packSize = 1,
}: PickerDebouncedProps) => {
  const [debouncedQuantity, setDebouncedQuantity] = usePickerDebounced({
    cachedQuantity,
    onUpdate: (count: number) => onQuantityChange(count),
  });

  const handleQuantityChange = (currentQuantity: number, increment: number, e?: SyntheticEvent) => {
    if (e != null) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (enabled) setDebouncedQuantity(currentQuantity + increment);
    else {
      onDisabledClick(currentQuantity, increment);
    }
  };

  const maxQuantityReached =
    debouncedQuantity >= availableQuantity || debouncedQuantity + packSize > availableQuantity;

  const buttonSize = size === 'small' ? 24 : 40;
  const iconSize = size === 'small' ? 18 : 22;

  return debouncedQuantity === 0 ? (
    cloneElement(emptyComponent, {
      onClick: (e?: SyntheticEvent) => {
        handleQuantityChange(0, packSize, e);
      },
    })
  ) : (
    <S.ButtonContainer className={className}>
      <>
        <S.RemoveFromCartActionButton
          icon="minus"
          iconSize={iconSize}
          aria-label={I18n.t('common.remove-product')}
          onClick={(e: SyntheticEvent) =>
            handleQuantityChange(
              debouncedQuantity,
              debouncedQuantity - packSize > 0 ? -packSize : -debouncedQuantity,
              e,
            )
          }
          variant="secondary"
          $buttonSize={buttonSize}
          disabled={debouncedQuantity === 0}
        />
        {children ? cloneElement(children, { children: debouncedQuantity }) : null}
      </>
      <ConditionalTooltip
        maxWidth={240}
        content={I18n.t('picker-debounced.sold-out')}
        disabled={!maxQuantityReached}>
        {/* Si le bouton est disabled alors il ne catch plus l'event qui peut bubble jusqu'à la carte produit et ouvrir la fiche produit */}
        <div
          role="button"
          tabIndex={-1}
          onClick={(e: SyntheticEvent) => {
            e.stopPropagation();
            e.preventDefault();
          }}>
          <S.AddToCartActionButton
            icon="plus"
            iconSize={iconSize}
            aria-label={I18n.t('common.add-product')}
            onClick={(e: SyntheticEvent) => {
              // La quantité disponible est arrondie au "packSize" inférieur
              // Ex: il reste véritablement 3 de disponible, le packSize est de 4, la quantité disponible reçue sera de 0
              handleQuantityChange(debouncedQuantity, packSize, e);
            }}
            disabled={maxQuantityReached}
            $buttonSize={buttonSize}
          />
        </div>
      </ConditionalTooltip>
    </S.ButtonContainer>
  );
};

export default PickerDebounced;
